import { IEnhancedTeamStats } from '@shared/types';
import { DocumentData, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions } from 'firebase/firestore';
import { breakUID, toSeasonWeekID } from 'helpers/IdHelpers';

// #region CONVERTERS *******************************************************************
export const teamMatchResultsToDoc = (team: IEnhancedTeamStats): DocumentData => {
  return {
    gameID: team.gameID,
    weekID: team.weekID,
    uID: team.uID,
    seasonWeekID: team.seasonWeekID ?? toSeasonWeekID(breakUID(team.uID).yearID, team.weekID),
    team: team.team,
    teamAbv: team.teamAbv,
    teamID: team.teamID,
    defensiveInterceptions: team.defensiveInterceptions,
    defensiveOrSpecialTeamsTds: team.defensiveOrSpecialTeamsTds,
    defTD: team.defTD,
    firstDowns: team.firstDowns,
    firstDownsFromPenalties: team.firstDownsFromPenalties,
    fourthDownEfficiency: team.fourthDownEfficiency,
    fumblesLost: team.fumblesLost,
    fumblesRecovered: team.fumblesRecovered,
    interceptionsThrown: team.interceptionsThrown,
    passCompletionsAndAttempts: team.passCompletionsAndAttempts,
    passingFirstDowns: team.passingFirstDowns,
    passingYards: team.passingYards,
    passingYardsAllowed: team.passingYardsAllowed,
    penalties: team.penalties,
    pointsScored: team.pointsScored,
    possession: team.possession,
    ptsAllowed: team.ptsAllowed,
    redZoneScoredAndAttempted: team.redZoneScoredAndAttempted,
    rushingAttempts: team.rushingAttempts,
    rushingFirstDowns: team.rushingFirstDowns,
    rushingYards: team.rushingYards,
    rushingYardsAllowed: team.rushingYardsAllowed,
    sacks: team.sacks,
    sacksAndYardsLost: team.sacksAndYardsLost,
    safeties: team.safeties,
    scoring2PtAgainst: team.scoring2PtAgainst || 0,
    scoring2PtFor: team.scoring2PtFor || 0,
    scoringEPAgainst: team.scoringEPAgainst || 0,
    scoringEPFor: team.scoringEPFor || 0,
    scoringFGAgainst: team.scoringFGAgainst || 0,
    scoringFGFor: team.scoringFGFor || 0,
    scoringPassingTDAgainst: team.scoringPassingTDAgainst || 0,
    scoringPassingTDFor: team.scoringPassingTDFor || 0,
    scoringRushingTDAgainst: team.scoringRushingTDAgainst || 0,
    scoringRushingTDFor: team.scoringRushingTDFor || 0,
    scoringTotalTDAgainst: team.scoringTotalTDAgainst || 0,
    scoringTotalTDFor: team.scoringTotalTDFor || 0,
    thirdDownEfficiency: team.thirdDownEfficiency,
    totalDrives: team.totalDrives,
    totalPlays: team.totalPlays,
    totalYards: team.totalYards,
    turnovers: team.turnovers,
    yardsPerPass: team.yardsPerPass,
    yardsPerPlay: team.yardsPerPlay,
    yardsPerRush: team.yardsPerRush,
    ydsAllowed: team.ydsAllowed,
  };
};

export const teamMatchResultsConverter: FirestoreDataConverter<IEnhancedTeamStats> = {
  toFirestore(team: IEnhancedTeamStats): DocumentData {
    return teamMatchResultsToDoc(team);
  },

  fromFirestore(docSnap: QueryDocumentSnapshot, options?: SnapshotOptions): IEnhancedTeamStats {
    const team = docSnap.data(options);

    return {
      gameID: team.gameID,
      weekID: team.weekID,
      uID: team.uID,
      seasonWeekID: team.seasonWeekID ?? toSeasonWeekID(breakUID(team.uID).yearID, team.weekID),
      team: team.team,
      teamAbv: team.teamAbv,
      teamID: team.teamID,
      defensiveInterceptions: team.defensiveInterceptions,
      defensiveOrSpecialTeamsTds: team.defensiveOrSpecialTeamsTds,
      defTD: team.defTD,
      firstDowns: team.firstDowns,
      firstDownsFromPenalties: team.firstDownsFromPenalties,
      fourthDownEfficiency: team.fourthDownEfficiency,
      fumblesLost: team.fumblesLost,
      fumblesRecovered: team.fumblesRecovered,
      interceptionsThrown: team.interceptionsThrown,
      passCompletionsAndAttempts: team.passCompletionsAndAttempts,
      passingFirstDowns: team.passingFirstDowns,
      passingYards: team.passingYards,
      passingYardsAllowed: team.passingYardsAllowed,
      penalties: team.penalties,
      pointsScored: team.pointsScored,
      possession: team.possession,
      ptsAllowed: team.ptsAllowed,
      redZoneScoredAndAttempted: team.redZoneScoredAndAttempted,
      rushingAttempts: team.rushingAttempts,
      rushingFirstDowns: team.rushingFirstDowns,
      rushingYards: team.rushingYards,
      rushingYardsAllowed: team.rushingYardsAllowed,
      sacks: team.sacks,
      sacksAndYardsLost: team.sacksAndYardsLost,
      safeties: team.safeties,
      scoring2PtAgainst: team.scoring2PtAgainst || 0,
      scoring2PtFor: team.scoring2PtFor || 0,
      scoringEPAgainst: team.scoringEPAgainst || 0,
      scoringEPFor: team.scoringEPFor || 0,
      scoringFGAgainst: team.scoringFGAgainst || 0,
      scoringFGFor: team.scoringFGFor || 0,
      scoringPassingTDAgainst: team.scoringPassingTDAgainst || 0,
      scoringPassingTDFor: team.scoringPassingTDFor || 0,
      scoringRushingTDAgainst: team.scoringRushingTDAgainst || 0,
      scoringRushingTDFor: team.scoringRushingTDFor || 0,
      scoringTotalTDAgainst: team.scoringTotalTDAgainst || 0,
      scoringTotalTDFor: team.scoringTotalTDFor || 0,
      thirdDownEfficiency: team.thirdDownEfficiency,
      totalDrives: team.totalDrives,
      totalPlays: team.totalPlays,
      totalYards: team.totalYards,
      turnovers: team.turnovers,
      yardsPerPass: team.yardsPerPass,
      yardsPerPlay: team.yardsPerPlay,
      yardsPerRush: team.yardsPerRush,
      ydsAllowed: team.ydsAllowed,
      snapCounts: team.snapCounts || {
        offense: 0,
        defense: 0,
        specialTeams: 0,
      },
    };
  },
};
// #endregion
